import type { MiceCandidate, MiceNeedView } from '@voll-intelligence/domain';
import { currentLocale } from '@web/i18n';

export const formatMoney = (money: { amount: number; currency: string }): string =>
  new Intl.NumberFormat(currentLocale(), { style: 'currency', currency: money.currency }).format(
    money.amount,
  );

/** Partida → chegada ("07:10 → 08:15"). `departAt`/`arriveAt` são horários locais com um `Z`
 *  espúrio: a hora sai do texto, nunca de `Date` (converter o fuso mudaria o horário mostrado). */
export const formatLegTimes = (flight: Pick<MiceCandidate, 'departAt' | 'arriveAt'>): string =>
  `${flight.departAt.slice(11, 16)} → ${flight.arriveAt.slice(11, 16)}`;

/** Dia e mês ("26/11", em en "11/26") de uma data AAAA-MM-DD, sem o fuso do navegador. */
export function formatShortDay(isoDate: string): string {
  const [y = 0, m = 1, d = 1] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(currentLocale(), {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Data completa ("12/03/1985", em en "03/12/1985") de uma data AAAA-MM-DD, sem o fuso do navegador. */
export function formatFullDate(isoDate: string): string {
  const [y = 0, m = 1, d = 1] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(currentLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Dia da semana e data curta ("qui., 12/11") de uma data AAAA-MM-DD, sem o fuso do navegador. */
export function formatLegDay(isoDate: string): string {
  const [y = 0, m = 1, d = 1] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(currentLocale(), {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Dia e hora ("18/10 14:02") de um instante de verdade (`createdAt`), no fuso de quem olha, como
 *  a atividade; os horários de voo, ao contrário, saem do texto. */
export const formatInstant = (iso: string): string =>
  new Intl.DateTimeFormat(currentLocale(), {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));

/** Os voos com as conexões ("LA 3410 + G3 1150"). */
export const formatFlights = (flight: Pick<MiceCandidate, 'hops'>): string =>
  flight.hops.map((h) => `${h.carrier} ${h.flightNumber}`).join(' + ');

export function formatRoute(need: MiceNeedView): string {
  const origins = need.outboundOrigins.join('/') || '—';
  return `${origins} · ${need.outboundDate ?? '—'} → ${need.returnDate ?? '—'}`;
}

/** O mesmo voo (cia, número e partida de cada trecho): ids de buscas diferentes não se comparam. */
export const sameFlight = (a: Pick<MiceCandidate, 'hops'>, b: Pick<MiceCandidate, 'hops'>) =>
  a.hops.length === b.hops.length &&
  a.hops.every((h, i) => {
    const o = b.hops[i];
    return (
      o?.carrier === h.carrier && h.flightNumber === o.flightNumber && h.departAt === o.departAt
    );
  });
